export type UserRole = 'super_admin' | 'admin' | 'coordinator' | 'coach' | 'student' | 'parent' | 'kid';

// Belt ranks for BJJ
export const beltRanks = ['White', 'Blue', 'Purple', 'Brown', 'Black'] as const;

export const kidBeltRanks = [
    'White',
    'Grey White',
    'Grey',
    'Grey Black',
    'Yellow White',
    'Yellow',
    'Yellow Black',
    'Orange White',
    'Orange',
    'Orange Black',
    'Green White',
    'Green',
    'Green Black'
] as const;

export type BeltRank = typeof beltRanks[number] | typeof kidBeltRanks[number];

// RBAC Types - Extended with granular actions
export type PermissionAction =
    // Standard CRUD
    | 'create'
    | 'read'
    | 'update'
    | 'delete'
    // Granular actions
    | 'approve'      // Approve pending items (users, belts, attendance)
    | 'reject'       // Reject/deny items
    | 'assign'       // Assign resources (subscriptions, classes, courses)
    | 'export'       // Export data/reports
    | 'import'       // Import bulk data
    | 'impersonate'  // Act as another user (admin only)
    | 'configure'    // Change system settings
    | 'moderate';    // Moderate content (feed posts, comments)

export type SystemModule =
    // --- DASHBOARD ---
    | 'dashboard_view'

    // --- USERS ---
    | 'users_view'
    | 'users_manage_students'
    | 'users_manage_coaches'
    | 'users_manage_staff'
    | 'users_manage_parents'

    // --- CLASSES ---
    | 'classes_view'
    | 'classes_manage'
    | 'schedule_view'
    | 'schedule_manage'

    // --- ATTENDANCE ---
    | 'attendance_view'
    | 'attendance_checkin'
    | 'attendance_manage' // Edit/Delete records

    // --- FINANCE ---
    | 'finance_view'
    | 'finance_income_view'
    | 'finance_income_manage'
    | 'finance_expenses_view'
    | 'finance_expenses_manage'
    | 'finance_payouts_view'
    | 'finance_payouts_manage'
    | 'finance_settings' // Categories, etc.

    // --- SUBSCRIPTIONS ---
    | 'subscriptions_view'
    | 'subscriptions_manage'
    | 'subscriptions_plans'

    // --- LMS ---
    | 'lms_view'
    | 'lms_manage'

    // --- BRANCHES ---
    | 'branches_view'
    | 'branches_manage'

    // --- ROLES ---
    | 'roles_view'
    | 'roles_manage'

    // --- SETTINGS ---
    | 'settings_general'
    | 'settings_profile' // Own profile

    // --- COMMUNICATION ---
    | 'feed_view'
    | 'feed_post'
    | 'feed_moderate'
    | 'messages_view'
    | 'messages_send'

    // --- ANALYTICS (NEW) ---
    | 'analytics_view'
    | 'analytics_export'

    // --- GAMIFICATION (NEW) ---
    | 'gamification_view'
    | 'gamification_manage'
    | 'gamification_belts'

    // --- KNOWLEDGE BASE (NEW) ---
    | 'kb_view'
    | 'kb_manage'

    // --- SUPPORT (NEW) ---
    | 'support_view'
    | 'support_inbox'
    | 'support_reply'

    // --- WALLET (NEW) ---
    | 'wallet_view'
    | 'wallet_manage'

    // --- AUDIT (NEW) ---
    | 'audit_view'
    | 'audit_manage'

    // --- NOTIFICATIONS (NEW) ---
    | 'notifications_view'
    // --- NOTIFICATIONS (NEW) ---
    | 'notifications_view'
    | 'notifications_manage'

    // --- FILES (NEW) ---
    | 'files_view'
    | 'files_manage'

    // --- WHATSAPP (Wazaio integration) ---
    | 'whatsapp_send'                 // Permission to trigger outbound WhatsApp messages.
    | 'whatsapp_templates_manage'     // Permission to CRUD the wazaioTemplates registry.

    // --- GAMIFICATION DETAILS ---
    | 'leaderboard_view'
    | 'challenges_view'
    | 'challenges_manage'
    | 'challenges_participate';

export const SYSTEM_MODULES: SystemModule[] = [
    'dashboard_view',
    'users_view', 'users_manage_students', 'users_manage_coaches', 'users_manage_staff', 'users_manage_parents',
    'classes_view', 'classes_manage', 'schedule_view', 'schedule_manage',
    'attendance_view', 'attendance_checkin', 'attendance_manage',
    'finance_view', 'finance_income_view', 'finance_income_manage', 'finance_expenses_view',
    'finance_expenses_manage', 'finance_payouts_view', 'finance_payouts_manage', 'finance_settings',
    'subscriptions_view', 'subscriptions_manage', 'subscriptions_plans',
    'lms_view', 'lms_manage',
    'branches_view', 'branches_manage',
    'roles_view', 'roles_manage',
    'settings_general', 'settings_profile',
    'feed_view', 'feed_post', 'feed_moderate', 'messages_view', 'messages_send',
    // New Modules
    'analytics_view', 'analytics_export',
    'gamification_view', 'gamification_manage', 'gamification_belts',
    'kb_view', 'kb_manage',
    'support_view', 'support_inbox', 'support_reply',
    'wallet_view', 'wallet_manage',
    'audit_view', 'audit_manage',
    'notifications_view', 'notifications_manage',
    'files_view', 'files_manage',
    'whatsapp_send', 'whatsapp_templates_manage',
    'leaderboard_view', 'challenges_view', 'challenges_manage', 'challenges_participate'
];

// Grouping for UI Display
export const PERMISSION_GROUPS: Record<string, { label: string, modules: SystemModule[] }> = {
    'Dashboard': {
        label: 'Dashboard',
        modules: ['dashboard_view']
    },
    'Analytics': {
        label: 'Analytics & Reports',
        modules: ['analytics_view', 'analytics_export']
    },
    'Users': {
        label: 'User Management',
        modules: ['users_view', 'users_manage_students', 'users_manage_coaches', 'users_manage_staff', 'users_manage_parents']
    },
    'Classes': {
        label: 'Classes & Schedule',
        modules: ['classes_view', 'classes_manage', 'schedule_view', 'schedule_manage']
    },
    'Attendance': {
        label: 'Attendance',
        modules: ['attendance_view', 'attendance_checkin', 'attendance_manage']
    },
    'Finance': {
        label: 'Finance & Accounting',
        modules: [
            'finance_view', 'wallet_view', 'wallet_manage',
            'finance_income_view', 'finance_income_manage',
            'finance_expenses_view', 'finance_expenses_manage',
            'finance_payouts_view', 'finance_payouts_manage',
            'finance_settings'
        ]
    },
    'Subscriptions': {
        label: 'Subscriptions',
        modules: ['subscriptions_view', 'subscriptions_manage', 'subscriptions_plans']
    },
    'Gamification': {
        label: 'Gamification & Belts',
        modules: ['gamification_view', 'gamification_manage', 'gamification_belts', 'leaderboard_view', 'challenges_view', 'challenges_manage', 'challenges_participate']
    },
    'LMS': {
        label: 'LMS (Learning)',
        modules: ['lms_view', 'lms_manage']
    },
    'KnowledgeBase': {
        label: 'Knowledge Base',
        modules: ['kb_view', 'kb_manage']
    },
    'Communication': {
        label: 'Social & Communication',
        modules: ['feed_view', 'feed_post', 'feed_moderate', 'messages_view', 'messages_send', 'notifications_view', 'notifications_manage']
    },
    'Support': {
        label: 'Support Center',
        modules: ['support_view', 'support_inbox', 'support_reply']
    },
    'Operations': {
        label: 'Operations & Settings',
        modules: ['branches_view', 'branches_manage', 'roles_view', 'roles_manage', 'audit_view', 'audit_manage', 'settings_general', 'files_view', 'files_manage', 'whatsapp_send', 'whatsapp_templates_manage']
    }

};

// Actions configuration per module
export const MODULE_ACTIONS: Record<SystemModule, PermissionAction[]> = {
    // Dashboard
    dashboard_view: ['read'],

    // Analytics
    analytics_view: ['read'],
    analytics_export: ['export'],

    // Users
    users_view: ['read', 'export'],
    users_manage_students: ['create', 'read', 'update', 'delete', 'approve', 'reject', 'import', 'export', 'assign'], // assign class/sub
    users_manage_coaches: ['create', 'read', 'update', 'delete', 'approve', 'reject', 'assign'],
    users_manage_staff: ['create', 'read', 'update', 'delete', 'approve', 'reject'],
    users_manage_parents: ['create', 'read', 'update', 'delete', 'approve', 'reject'],

    // Classes
    classes_view: ['read'],
    classes_manage: ['create', 'read', 'update', 'delete'],
    schedule_view: ['read'],
    schedule_manage: ['create', 'read', 'update', 'delete'], // maybe 'assign' (coach)

    // Attendance
    attendance_view: ['read', 'export'],
    attendance_checkin: ['create', 'update'], // checkin is create
    attendance_manage: ['create', 'read', 'update', 'delete', 'approve', 'reject', 'import', 'export'],

    // Finance
    finance_view: ['read', 'export'],
    finance_income_view: ['read', 'export'],
    finance_income_manage: ['create', 'read', 'update', 'delete', 'import'],
    finance_expenses_view: ['read', 'export'],
    finance_expenses_manage: ['create', 'read', 'update', 'delete', 'import'],
    finance_payouts_view: ['read', 'export'],
    finance_payouts_manage: ['create', 'read', 'update', 'delete', 'approve', 'reject'],
    finance_settings: ['read', 'update', 'configure'],
    wallet_view: ['read', 'export'],
    wallet_manage: ['create', 'read', 'update', 'delete', 'approve', 'reject'], // manual credit/debit

    // Subscriptions
    subscriptions_view: ['read', 'export'],
    subscriptions_manage: ['create', 'read', 'update', 'delete', 'assign'],
    subscriptions_plans: ['create', 'read', 'update', 'delete'],

    // Gamification
    gamification_view: ['read'],
    gamification_manage: ['create', 'read', 'update', 'delete', 'checkin' as any, 'approve', 'reject'], // 'checkin' for awarding points manually? no 'create'
    gamification_belts: ['read', 'approve', 'reject'],
    leaderboard_view: ['read', 'export'],
    challenges_view: ['read'],
    challenges_manage: ['create', 'read', 'update', 'delete'],
    challenges_participate: ['create', 'update'], // participate, update progress

    // Files
    files_view: ['read'],
    files_manage: ['create', 'read', 'update', 'delete'],
    whatsapp_send: ['create'],
    whatsapp_templates_manage: ['create', 'read', 'update', 'delete'],

    // LMS
    lms_view: ['read'],
    lms_manage: ['create', 'read', 'update', 'delete', 'assign'], // assign courses

    // Knowledge Base
    kb_view: ['read'],
    kb_manage: ['create', 'read', 'update', 'delete'],

    // Branches
    branches_view: ['read'],
    branches_manage: ['create', 'read', 'update', 'delete'],

    // Roles
    roles_view: ['read'],
    roles_manage: ['create', 'read', 'update', 'delete'],

    // Audit
    audit_view: ['read', 'export'],
    audit_manage: ['read', 'export', 'delete'], // delete logs? maybe not.

    // Settings
    settings_general: ['read', 'update', 'configure'],
    settings_profile: ['read', 'update'],

    // Communication
    feed_view: ['read'],
    feed_post: ['create', 'read', 'update', 'delete'],
    feed_moderate: ['read', 'delete', 'moderate'], // moderate allows hiding/flagging
    messages_view: ['read'],
    messages_send: ['create'], // send is create
    notifications_view: ['read'],
    notifications_manage: ['create', 'read', 'update', 'delete'], // create notification

    // Support
    support_view: ['read'],
    support_inbox: ['read', 'export'],
    support_reply: ['read', 'update', 'approve', 'reject'], // reply is update/create. 'approve' could be resolve.
};

export interface ModulePermissions {
    create: boolean;
    read: boolean;
    update: boolean;
    delete: boolean;
    // Optional granular permissions
    approve?: boolean;
    reject?: boolean;
    assign?: boolean;
    export?: boolean;
    import?: boolean;
    impersonate?: boolean;
    configure?: boolean;
    moderate?: boolean;
    [key: string]: boolean | undefined; // Allow for other custom actions
}
export interface Role {
    id: string;
    name: string;
    description?: string;
    icon?: string; // Icon name from Lucide
    color?: string; // Hex color code
    permissions: Record<SystemModule, ModulePermissions>;
    isSystem?: boolean; // Built-in roles can't be deleted
    isGamified?: boolean; // Participation in points/badges/challenges
    createdAt: Date;
    updatedAt: Date;
}

export type AccountStatus = 'pending' | 'active' | 'rejected' | 'archived';

export interface User {
    /** The plan this member asked for at signup. A REQUEST — no subscription exists until an
     *  admin activates one after payment. `requestedPlanId` is 'undecided' when they chose
     *  "not sure yet". (24 Sept 2026) */
    requestedPlanId?: string;
    requestedPlanName?: string;
    requestedPlanPrice?: number;
    requestedPlanDurationDays?: number | null;
    requestedPlanAt?: Date;
    uid: string;
    email: string;  // Optional for kid profiles
    displayName: string | null;
    photoURL: string | null;
    status?: AccountStatus; // Optional for backward compatibility, defaults to 'active' logic if missing
    rejectionReason?: string; // Reason for rejection if status is 'rejected'
    role: UserRole; // Legacy field for backwards compatibility
    roleId?: string; // Reference to custom Role document
    roleIds?: string[]; // Multiple roles support (document IDs)
    roleNames?: string[]; // Resolved role names (e.g., 'coordinator', 'coach')
    permissions?: Record<SystemModule, ModulePermissions>; // Calculated effective permissions
    branchId?: string;
    assignedCoachId?: string; // Coach responsible for this student
    subscriptionStatus?: 'active' | 'expired' | 'cancelled';
    activeSubscriptionId?: string;

    // Kid/Parent fields
    /**
     * @deprecated RETIRED 17 Sept 2026 — neither read nor written. Set true on only 8
     * of the 31 kids; still present on old documents. Use `isKidMember(user)`
     * (`role === 'kid'`) for division. Removing the field from those documents is a
     * data change that needs Ahmed's list first.
     */
    isKid?: boolean;
    /** Server-assigned code this member shares (C2). Members cannot change it. */
    referralCode?: string;
    /** The code this member typed at signup — whose referral they are. */
    referredByCode?: string;
    parentId?: string; // Links kid to parent account
    /**
     * Denormalized list of the parent's child UIDs. Populated on every
     * parent doc by the Jun 1 2026 backfill and maintained going forward
     * by all kid-creation code paths (AddKidsStep, AddKidDialog,
     * CreateUserDialog, and the createChildAccount Cloud Function).
     *
     * Powers `useFamily`'s primary child-resolution path — batched
     * `documentId() in [...]` query against the users collection — and
     * the parent-row family-total aggregation in WalletBalanceDisplay.
     * Faster and more resilient than the legacy `kid.parentId == X`
     * reverse-lookup query, which has been observed to silently return
     * zero results in some production sessions.
     *
     * Optional so kid docs and non-parent docs stay valid.
     */
    childrenIds?: string[];
    belt?: string; // Denormalized belt for kid profiles
    stripes?: number; // Belt stripes (0-4)

    // FAMILY ACCOUNT MODEL - NEW FIELDS
    isKidProfile?: boolean; // True for kids managed by parents (no credentials required)
    isStudent?: boolean; // True if this user (e.g. parent) also participates as a student
    /**
     * Multi-role boolean flags. Mirror the existing `isStudent` /
     * `isKid` pattern. Read alongside the legacy `role` string and the
     * newer `roleIds` array by the helpers in src/utils/roleChecks.ts,
     * which try each shape in turn for backward compatibility.
     *
     * Optional — legacy docs won't have these set. Absent = false.
     */
    isParent?: boolean;
    isCoach?: boolean;
    isAdmin?: boolean;
    age?: number; // Age for kids - enables age-based features
    gender?: 'male' | 'female' | 'other'; // New gender field
    hasOwnLogin?: boolean; // False = parent manages, true = independent teen account

    mobile?: string;
    totalScore?: number; // Gamification score
    followersCount?: number;
    followingCount?: number;
    dateOfBirth?: Date; // For birthday greetings
    tours?: Record<string, { completed: boolean; completedAt?: Date }>; // Guided tour completion tracking

    // Wazaio WhatsApp integration (Issue: WhatsApp integration)
    // True if the user has opted in to receive WhatsApp messages from the academy.
    // Default for new signups = false. All pre-integration users were backfilled
    // to true (treated as opted-in since they joined before the opt-in field
    // existed; per product decision they get grandfathered).
    whatsappOptIn?: boolean;
    // True if the user has opted in to receive automated VOICE CALLS from the
    // academy (retention / onboarding). Independent of whatsappOptIn, but seeded
    // from it on backfill (a user who accepted WhatsApp is presumed OK with
    // calls unless they opt out). Never dial without this = true.
    voiceCallOptIn?: boolean;
    // Server-side stripe progression tracking (Issue #2)
    lastStripeAwardedAt?: Date | null;
    beltTestReadyNotifiedAt?: Date | null;

    createdAt: Date;
    updatedAt: Date;
}

export interface CompetitionResult {
    id: string;
    competitionName: string;
    date: Date;
    location?: string;
    division?: string; // e.g. "Adult / Blue / Middle"
    weightClass?: string;
    placement?: 'Gold' | 'Silver' | 'Bronze' | 'Participant' | 'DQ';
    notes?: string;
}

export interface InjuryRecord {
    id: string;
    description: string;
    date: Date;
    status: 'active' | 'recovering' | 'healed';
    notes?: string;
}

export interface StudentProfile {
    userId: string;
    username?: string; // For @username public profile URL
    // Level 1 Profile Data
    firstName?: string;
    lastName?: string;
    nickname?: string;
    dateOfBirth?: Date;
    gender?: 'male' | 'female' | 'other';
    nationality?: string;
    phoneNumber?: string;
    phone?: string; // Alias for compatibility
    status?: 'active' | 'inactive' | 'suspended';
    profilePhotoURL?: string;
    coverPhotoURL?: string;

    // BJJ Specific
    belt: BeltRank;
    stripes?: number; // 0-4
    joinDate?: Date; // Member since

    // Level 2: Physical
    weight?: number;
    height?: number;
    competitionWeightClass?: string;
    bodyComposition?: string;

    // Level 2: Training & Experience
    yearsTraining?: number;
    previousGym?: string;
    primaryPosition?: string; // e.g. "Guard", "Top Pressure"
    favoriteSubmissions?: string[];
    competitionPreference?: 'gi' | 'nogi' | 'both';
    bio?: string;

    // Level 2: Records
    competitionHistory?: CompetitionResult[];
    injuryHistory?: InjuryRecord[];

    // Level 2: Stats (Manual or Calculated)
    totalMatches?: number;
    wins?: number;
    losses?: number;
    draws?: number;

    // Media
    gallery?: string[];
    achievements?: string[]; // Legacy simple string array
    socialLinks?: {
        instagram?: string;
        facebook?: string;
        twitter?: string;
        youtube?: string;
        tiktok?: string;
    };
    branchId?: string;
    createdAt?: Date;
    updatedAt?: Date;
    lastPromotionDate?: Date; // Added for promotion tracking
}

// Promotion & Technical Evaluation
export interface PromotionRequirement {
    id: string; // requirementDocId(belt, stripe, division) — "White_0", kids on White "White_0_kids"
    belt: BeltRank;
    division?: 'adults' | 'kids'; // absent on documents written before 19 Sept 2026
    stripe: number; // 0-4
    requiredClasses: number; // Non-continuous total classes
    requiredConsecutiveClasses: number; // Continuous attendance classes
    requiredStreakWeeks: number; // DEPRECATED: Keeping for legacy, replaced by consecutive classes logic
    classesPerWeek: number; // Minimum classes per week to count for streak
    maxAbsenceWeeks: number; // Reset consecutive count if absent for X weeks (e.g., 12)
}

export type TechnicalStatus = 'ready' | 'needs_work' | 'improving' | 'mastered';

export interface TechnicalSkill {
    name: string;
    rating: number; // 1-5
    status: TechnicalStatus;
    notes?: string;
}

export interface TechnicalEvaluation {
    id: string;
    userId: string;
    coachId: string;
    date: Date;
    skills: TechnicalSkill[];
    overallScore: number; // 0-5
    statusLabel: string; // e.g. "On track for 3rd stripe"
    nextMilestone: string; // e.g. "Technical evaluation for Blue Belt"
    notes?: string;
}

export interface CoachProfile {
    userId: string;
    bio?: string;
    specialties?: string[];
    certifications?: string[];
    experience?: number;
    branchId?: string;
    createdAt?: Date;
    updatedAt?: Date;
}

// LMS Types
export type ContentType = 'video' | 'document' | 'text' | 'quiz';
export type CourseDifficulty = 'beginner' | 'intermediate' | 'advanced' | 'all_levels';
export type CourseCategory = 'fundamentals' | 'techniques' | 'competition' | 'theory' | 'conditioning';

// Course Prerequisites
export interface CoursePrerequisites {
    enabled: boolean;              // Toggle to enable/disable prerequisites check
    matchMode: 'all' | 'any';      // Must meet ALL prerequisites or ANY single one
    minimumBelt?: string;          // e.g., 'blue', 'purple', 'brown'
    allowedRoles?: string[];       // e.g., ['student', 'coach'] - empty means all roles
    minimumPoints?: number;        // Minimum total score points required
}

export interface LMSCourse {
    id: string;
    slug?: string; // URL friendly identifier
    title: string;
    description: string;
    instructorId: string;
    instructorName?: string; // Denormalized
    difficulty: CourseDifficulty;
    category: CourseCategory;
    thumbnail?: string;
    duration?: number; // Total duration in minutes
    price?: number; // Price in default currency (0 for free)
    enrolledCount: number;
    isPublished: boolean;
    allowedUserIds?: string[];
    requirements?: string[]; // Legacy: e.g., ["Blue Belt", "Basic Fundamentals Course"]
    prerequisites?: CoursePrerequisites; // New structured prerequisites
    createdAt: Date;
    updatedAt: Date;
}

export interface Module {
    id: string;
    courseId: string;
    title: string;
    description?: string;
    order: number;
    createdAt: Date;
}

export interface Lesson {
    id: string;
    moduleId: string;
    courseId: string;
    title: string;
    description?: string;
    contentType: ContentType;
    contentUrl?: string | null; // YouTube/Vimeo URL or file path
    content?: string | null; // Text content for text lessons
    duration?: number; // Duration in minutes
    order: number;
    isFree: boolean; // Can be previewed without enrollment
    createdAt: Date;
    updatedAt: Date;
}

export interface CourseEnrollment {
    id: string;
    userId: string;
    userName?: string; // Denormalized
    courseId: string;
    courseTitle?: string; // Denormalized
    enrolledAt: Date;
    completedAt?: Date;
    progress: number; // 0-100
    status: 'active' | 'completed' | 'dropped';
}

export interface LessonCompletion {
    id: string;
    userId: string;
    lessonId: string;
    courseId: string;
    completedAt: Date;
}

export interface Branch {
    id: string;
    name: string;
    address: string;
    phone: string;
    email: string;
    commissionPercentage?: number; // 0-100
    settings?: Record<string, unknown>;
    createdAt: Date;
}

export type ClassType = 'gi' | 'nogi' | 'striking' | 'open_mat' | 'competition' | 'kids';

export interface ClassSchedule {
    dayOfWeek: number; // 0-6 (Sunday-Saturday)
    startTime: string; // HH:mm format
    endTime: string;
    duration: number; // minutes
}

export interface Class {
    id: string;
    name: string;
    type: ClassType;
    description?: string;
    schedules: ClassSchedule[]; // Array of schedules
    capacity: number;
    enrolled: number; // Current enrollment count
    branchId: string;
    coachId: string;
    coachName?: string; // Denormalized for display
    isRecurring: boolean;
    status: 'active' | 'cancelled' | 'completed' | 'deleted';
    createdAt: Date;
    updatedAt: Date;
}

export interface CommissionDues {
    id: string;
    coachId: string; // Recipient
    coachName: string;
    studentId: string;
    studentName: string;
    amount: number; // Calculated Commission
    payoutSource: 'subscription' | 'drop_in' | 'class_pack';
    sourceId: string; // SubscriptionID or PaymentID
    classId?: string; // The class that triggered the commission (Optional now)
    className?: string;
    branchId: string;
    /**
     * `settled_externally` = the money reached the coach outside this system and no expense
     * belongs here. Used for the 245 dues up to 31 Aug 2026, which the monthly salary
     * payments already covered (Ahmed, 28 Sept 2026). Never write `paid` for those — that
     * means "this system paid it" and would double-count against Salaries.
     */
    status: 'pending' | 'paid' | 'settled_externally';
    createdAt: Date; // Date of attendance/trigger
    paidAt?: Date; // If paid out
    settledAt?: Date;          // When it was closed as settled outside the system
    settledThrough?: string;   // The cut-off it was closed under, YYYY-MM-DD
    settledReason?: string;    // Why, in words: "covered by monthly salary payments"
    settledBy?: string;        // The uid that authorised it
    settledByName?: string;
    paymentId?: string; // ID of the specific payout transaction
    notes?: string;
}

export interface Enrollment {
    id: string;
    userId: string;
    userName?: string; // Denormalized
    classId: string;
    className?: string; // Denormalized
    enrolledAt: Date;
    status: 'active' | 'cancelled';
}

export interface AttendanceRecord {
    id: string;
    userId: string;
    userName?: string; // Denormalized
    classId: string;
    className?: string; // Denormalized
    date: Date; // Date of the class
    checkInTime: Date;
    checkOutTime?: Date;
    status: 'present' | 'absent' | 'late' | 'checked_out';
    approvalStatus: 'approved' | 'pending' | 'rejected';
    recordedBy: 'admin' | 'coach' | 'student'; // Who recorded this
    recordedById: string; // UID of who recorded
    approvedById?: string; // UID of who approved
    approvedAt?: Date;
    markedBy: string; // Legacy - Coach who marked attendance
    notes?: string;
    branchId?: string;

    // Coach Workflow Fields
    techniqueRating?: number; // 1-5
    effortRating?: number; // 1-5
    understandingRating?: number; // 1-5
    coachNotes?: string;

    createdAt: Date;
}

export interface SubscriptionPlan {
    id: string;
    name: string;
    /** The name Arabic-speaking members see. Optional: falls back to `name`. */
    nameAr?: string;
    description?: string;
    type: 'unlimited' | 'class_count'; // Legacy?
    planType: 'recurring' | 'punch_card' | 'unlimited'; // Actual usage
    price: number;

    // Usage in code varies, unifying:
    duration: number; // days (legacy?)
    durationDays: number; // days (actual usage)

    classQuota?: number | null; // null for unlimited (legacy?)
    classLimit: number | null; // null for unlimited (actual usage)

    features: string[];
    isActive: boolean;
    isPublic?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
}

export interface Subscription {
    id: string;
    userId: string;
    planId: string;
    assignedCoachId?: string; // Coach getting commission for this sub
    status: 'active' | 'cancelled' | 'expired';
    startDate: Date;
    endDate: Date;
    remainingClasses?: number;
    commissionProcessed?: boolean; // NEW: Indicates if commission has been generated for this period
    commissionPayoutId?: string; // Link to the generated payout record
}

// Task #44 — Churn & retention analytics
// Slice of a metric block for a particular dimension value (branch, plan type, role).
export interface ChurnSlice {
    activeAtPeriodStart: number;
    expired: number;
    cancelled: number;
    expirationChurnRate: number;
    cancellationChurnRate: number;
    totalChurnRate: number;
    retentionRate: number;
}

// Cohort row in the retention heatmap.
export interface ChurnCohortRow {
    cohortMonth: string; // 'YYYY-MM' — the signup month of this cohort
    cohortSize: number;
    retention: {
        m1: number;
        m2: number;
        m3: number;
        m6: number;
        m12: number;
    };
}

export interface ChurnMetricsDoc {
    period: string; // 'YYYY-MM' — the reporting month
    computedAt: Date; // serverTimestamp in Firestore; Date on the client after toDate()
    // Headline rates for the period.
    expirationChurnRate: number;
    cancellationChurnRate: number;
    totalChurnRate: number;
    retentionRate: number;
    // Cohort retention heatmap — last 12 signup months ending at this period.
    cohorts: ChurnCohortRow[];
    // Leading indicators.
    atRiskCount: number; // active subs with no attendance in 14d+
    averageLifetimeDays: number; // among subs churned in the last 365d
    reactivationRate30d: number;
    reactivationRate60d: number;
    reactivationRate90d: number;
    // Dimension slicing.
    byBranch: Record<string, ChurnSlice>;
    byPlanType: Record<'recurring' | 'punch_card' | 'unlimited', ChurnSlice>;
    byRole: { adult: ChurnSlice; kid: ChurnSlice };
}

export interface Payment {
    id: string;
    userId: string;
    amount: number;
    status: 'pending' | 'completed' | 'failed' | 'refunded';
    planId?: string;
    timestamp: Date;
}

// Gamification Types
export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type ChallengeType =
    // Account & Profile
    | 'account_created'        // User creates an account
    | 'profile_completed'      // User completes their profile (photo, bio, etc.)
    | 'first_login'            // User logs in for the first time
    // Attendance
    | 'attendance'             // Attend X classes
    | 'first_checkin'          // First class check-in
    | 'streak'                 // Attendance streak (X days in a row)
    | 'weekly_attendance'      // Attend X classes in a week
    | 'monthly_attendance'     // Attend X classes in a month
    // LMS / Learning
    | 'course_enrolled'        // Enroll in a course
    | 'course_completed'       // Complete an entire course
    | 'lesson_completed'       // Complete X lessons
    | 'first_lesson'           // Complete first lesson
    // Social & Community
    | 'first_post'             // Create first feed post
    | 'posts_created'          // Create X posts
    | 'comments_made'          // Make X comments
    | 'followers_gained'       // Gain X followers
    | 'following_users'        // Follow X users
    | 'grip_someone'           // Grip (follow) another user
    | 'get_gripped'            // Get gripped (followed) by someone
    // Gamification
    | 'points_earned'          // Earn X total points
    | 'badges_earned'          // Earn X badges
    | 'leaderboard_rank'       // Reach top X on leaderboard
    | 'monthly_hero'           // Become monthly hero
    // Competition & Events
    | 'competition'            // Competition-related challenges
    | 'event_attended'         // Attend X special events
    // Belt Progression
    | 'belt_promotion'         // Get promoted to next belt
    // Subscription & Payments
    | 'subscription_purchased' // Purchase a subscription
    | 'referral_made'          // Refer a new student
    // Milestones
    | 'milestone'              // Generic milestone (e.g., 1 year anniversary)
    | 'anniversary'            // Account anniversary
    | 'check_in'               // Reward for every check-in
    | 'star_of_class'          // Coach's pick of one present member per session (server-awarded)
    // Post-Class Feedback
    | 'self_feedback'          // Student rates their own performance after class
    | 'peer_feedback'          // Student rates peers after class
    // Custom
    | 'custom';                // Admin-defined custom challenge

export interface AuditLogEntry {
    id: string;
    action: string; // e.g., 'user.update', 'class.delete'
    module: string; // e.g., 'users', 'finance'
    actorId: string; // User who performed the action
    actorName?: string;
    targetId?: string; // ID of the affected object
    targetName?: string; // Name/Label of affected object
    details?: string; // JSON string or text summary
    ipAddress?: string;
    userAgent?: string;
    createdAt: Date;
    status: 'success' | 'failure';
}

export interface Badge {
    id: string;
    name: string;
    description: string;
    icon: string; // emoji or icon name
    color: string;
    rarity: BadgeRarity;
    createdAt: Date;
}

export interface ChallengeRewards {
    badgeId?: string;
    walletBalance?: {
        amount: number;
        scope: WalletScope;
        scopeId?: string;
    };
    classCredit?: {
        count: number;
    };
    scorePoints?: number;
}

export interface Challenge {
    id: string;
    name: string;
    description: string;
    type: ChallengeType;
    targetValue: number; // e.g., attend 10 classes
    // rewards field replaces legacy badgeId
    rewards?: ChallengeRewards;
    /** @deprecated Use rewards.badgeId instead */
    badgeId?: string;
    isActive: boolean;
    isRepeatable?: boolean; // If true, can be completed multiple times (e.g., grip challenge)
    expiresAt?: Date;
    createdAt: Date;
}

// Level System
export interface Level {
    id: string;
    level: number;              // Level number (1, 2, 3...)
    name: string;               // Display name (e.g., "White Belt", "Novice")
    pointsRequired: number;     // XP needed to reach this level
    rewards?: ChallengeRewards; // UNUSED — nothing grants it on reaching a level; perks replace it (19 Sept 2026)
    /** What holding this level gives the member (Ahmed, 19 Sept 2026). Each level lists its own
     *  full set — perks are not inherited from lower levels. */
    perks?: LevelPerk[];
    icon?: string;              // Emoji or icon
    color?: string;             // Color for display
    createdAt: Date;
}

/**
 * A level perk. Recognition and access perks are delivered by people (a badge on the profile,
 * a guest pass at the desk); a discount perk is read by the app at checkout — so it carries
 * its percentage and what it applies to. Renewal discounts are capped at 10% (Ahmed).
 */
export type LevelPerk =
    | { kind: 'recognition' | 'access'; label: string; labelAr?: string }
    | { kind: 'discount'; percent: number; appliesTo: 'store' | 'renewal' };

export interface UserBadge {
    id: string;
    userId: string;
    badgeId: string;
    earnedAt: Date;
    challengeId?: string;
}

export interface UserChallengeProgress {
    id: string;
    userId: string;
    challengeId: string;
    currentValue: number;
    isCompleted: boolean;
    completedAt?: Date;
    startedAt: Date;
}

// Wallet Types
export interface Wallet {
    userId: string;
    balance: number;
    currency: string;
    updatedAt: Date;
}

export type TransactionType = 'credit' | 'debit';
export type WalletScope = 'all' | 'lms' | 'subscriptions' | 'store' | 'specific_item';

export interface WalletTransaction {
    id: string;
    walletId: string;
    userId: string;
    type: TransactionType;
    amount: number;
    description: string;
    // New fields for scope restrictions
    scope?: WalletScope;
    scopeId?: string; // ID of specific item if scope is 'specific_item'
    isRefundable?: boolean;
    // Expiry fields for credits
    expiryDate?: Date; // If set, this credit expires on this date

    performedBy: string; // User ID of admin/system
    relatedEntityId?: string; // ID of subscription, order, etc.
    timestamp: Date;
}

// --- Feed & Social ---

export type PostType = 'user_post' | 'admin_announcement' | 'system_announcement' | 'birthday_greeting';

export interface Comment {
    id: string;
    postId: string;
    authorId: string;
    authorName: string;
    authorphotoURL?: string;
    content: string;
    likes?: string[]; // Array of user IDs who liked the comment
    createdAt: Date;
}

export interface Post {
    id: string;
    type: PostType;
    authorId: string; // If system/birthday, might be 'system'
    authorName: string;
    authorPhotoURL?: string;
    authorBelt?: string; // For BeltAvatar display
    authorStripes?: number; // For BeltAvatar stripes
    title?: string; // Mainly for announcements
    content: string;
    images?: string[]; // URLs
    likes: string[]; // Array of user UIDs
    commentsCount: number;
    // For 'birthday_greeting', we might store the celebrant's UID
    metadata?: {
        celebrantId?: string;
    };
    createdAt: Date;
}

// --- Gamification History ---

export type PointTransactionType = 'manual_adjustment' | 'challenge_reward' | 'check_in';

export interface PointTransaction {
    id: string;
    userId: string;
    moderatorId?: string; // Null if system
    type: PointTransactionType;
    amount: number;
    description: string;
    /** Training XP (leaderboard, Hero) or Lifetime-only. Absent before 19 Sept 2026 —
     *  classify those with xpTrackOf (src/lib/xpTrack.ts). */
    track?: 'training' | 'lifetime';
    createdAt: Date;
}

export interface MonthlyHero {
    id: string; // YYYY-MM or YYYY-MM-adults for adults
    userId: string;
    userName: string;
    userPhotoURL: string | null;
    score: number;
    attendanceCount: number;
    month: string; // "January", etc.
    year: number;
    branchId?: string;
    isKidHero: boolean; // true = kids pool, false = adults pool
    createdAt: Date;
    // ── v2 (18 Sept 2026, functions/src/heroes.ts) ──────────────────────────
    // Written by the server only. `heroes` holds EVERY hero for the month: an exact
    // tie is a joint award, never a coin-flip. The legacy single-hero fields above
    // are kept filled (userName joins the names) for older clients.
    division?: 'adults' | 'kids';
    month0?: number;
    joint?: boolean;
    heroes?: MonthlyHeroEntry[];
    tieBrokenByTime?: boolean;
    basisVersion?: string;
}

export interface MonthlyHeroEntry {
    userId: string;
    userName: string;
    userPhotoURL: string | null;
    branchId: string | null;
    /** A coach keeps this monthly title but cannot take Hero of the Year (27 Sept 2026). */
    isCoach?: boolean;
    /** The academy carried this member all month: superseded, and the annual race skips it. */
    wasFullySponsored?: boolean;
    xp: number;                  // XP counted for ranking
    sessions: number;
    excludedBulkXp: number;      // bulk-entered sessions — never counted, balance untouched
    excludedDuplicateXp: number; // repeated once-per-member awards (Eid Seminar)
    /** XP paid this month for sessions from an earlier, closed month (from 19 Sept 2026). */
    excludedLateSessionXp?: number;
    reachedAt: string;
}

// --- Social & Messaging ---

export interface Follow {
    id: string; // followerId_followingId
    followerId: string;
    followingId: string;
    createdAt: Date;
}

export interface Conversation {
    id: string;
    participants: string[]; // [uid1, uid2]
    lastMessage?: string;
    lastMessageAt?: Date;
    lastSenderId?: string;
    unreadCounts: Record<string, number>; // { [uid]: count }
    createdAt: Date;
    updatedAt: Date;
}

export interface Message {
    id: string;
    conversationId: string;
    senderId: string;
    content: string;
    createdAt: Date;
    readBy: string[];
}

export interface PublicProfileData {
    user: User | null;
    profile: StudentProfile | null;
}

// --- Notifications ---

export type NotificationType = 'like' | 'comment' | 'follow' | 'system' | 'broadcast' | 'class_reminder' | 'achievement' | 'wallet_credit' | 'wallet_debit' | 'wallet_expiry_warning' | 'stripe_earned' | 'belt_test_ready_attendance' | 'stripe_eligible';

export interface Notification {
    id: string;
    userId: string; // Recipient
    type: NotificationType;
    title: string;
    message: string;
    link?: string; // URL to navigate to
    isRead: boolean;
    createdAt: Date;
    metadata?: {
        actorId?: string; // Who triggered it
        actorName?: string;
        actorPhotoURL?: string;
        entityId?: string; // Post ID, Comment ID, etc.
    };
}

// --- Coach Commissions ---

export interface CoachPayoutRecord {
    id: string;
    studentId: string;
    generatedAt: Date;
    paidAt?: Date; // If paid out
}

// --- Coach Post-Class Workflow ---

export interface CoachClassContext {
    id: string; // Typically `${classId}_${format(date, 'yyyy-MM-dd')}`
    classId: string;
    className: string; // Denormalized
    date: Date; // Class start time
    coachId: string;
    chapter: string; // e.g. "Mount", "Guard"
    focusAreas: string[]; // e.g. ["Escapes", "Controls"]
    keywords?: string[];
    description?: string;
    createdAt: Date;
}

export interface StudentClassRating {
    techniqueRating: number; // 1-5
    effortRating: number; // 1-5
    understandingRating: number; // 1-5
    notes?: string;
}

// --- Post-Class Feedback ---

export interface AssessmentRating {
    technique: number; // 1-5
    effort: number; // 1-5
    understanding: number; // 1-5
}

export interface PeerRating {
    userId: string;
    userName: string;
    userPhotoURL?: string;
    ratings: AssessmentRating;
}

export interface ClassFeedback {
    id: string;
    userId: string;
    classId: string;
    className: string;
    date: Date;
    selfAssessment: AssessmentRating;
    peerRatings: PeerRating[]; // Up to 3
    /** How many classmates the widget offered to rate (from 19 Sept 2026) — tells "none were
     *  offered" apart from "offered and skipped". */
    peersOffered?: number;
    xpEarned: number; // display only — the server awards (onClassFeedbackCreated)
    createdAt: Date;
}

// --- LMS Q&A & Notes ---

export interface LessonQuestionReply {
    id: string;
    userId: string;
    userName: string;
    userPhotoURL?: string;
    role: UserRole; // To distinguish admin/coach replies
    content: string;
    createdAt: Date;
}

export interface LessonQuestion {
    id: string;
    lessonId: string;
    userId: string;
    userName: string;
    userPhotoURL?: string;
    content: string;
    createdAt: Date;
    replies: LessonQuestionReply[];
    isResolved?: boolean;
}

export interface LessonNote {
    id: string; // usually composite userId_lessonId or auto-id
    userId: string;
    lessonId: string;
    content: string;
    updatedAt: Date;
}

// --- Knowledgebase ---

export interface KbCategory {
    id: string;
    title: string;
    slug: string; // URL friendly
    description?: string;
    order: number;
    allowedRoles: UserRole[]; // e.g. ['student', 'coach'] or empty for all
    isPublic: boolean; // Accessible without login? or just generally visible
    language?: 'en' | 'ar' | 'both'; // Language filter for category visibility
    createdAt: Date;
    updatedAt: Date;
}

export interface KbArticle {
    id: string;
    categoryId: string;
    categorySlug: string; // Denormalized for URL construction
    title: string;
    slug: string;
    content: string; // HTML from ReactQuill
    authorId: string;
    authorName: string;
    isPublic: boolean;
    allowedRoles?: string[]; // If empty, visible to all (if public/authed checks pass)
    language: 'en' | 'ar' | 'both'; // 'both' means visible in all languages
    createdAt: Date;
    updatedAt: Date;
}

// --- Belt Promotions ---

export type PromotionStatus = 'pending' | 'approved' | 'rejected';



export interface BeltRequest {
    id: string;
    userId: string;
    userName: string;
    userPhotoURL?: string;
    currentBelt: string;
    currentStripes: number;
    requestedBelt: string;
    requestedStripes: number;
    status: PromotionStatus;
    rejectionReason?: string;
    createdAt: Date;
    processedAt?: Date;
    processedBy?: string; // Admin ID
}

// --- Support / Communication ---

export type SupportRequestType = 'suggestion' | 'feature_request' | 'bug_report' | 'inquiry';
export type SupportRequestStatus = 'new' | 'in_progress' | 'resolved' | 'closed';

export interface SupportReply {
    id: string;
    authorId: string;
    authorName: string;
    content: string;
    createdAt: Date;
}

export interface SupportRequest {
    id: string;
    type: SupportRequestType;
    subject: string;
    content: string;
    isAnonymous: boolean;

    // Sender info (undefined if anonymous)
    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;

    // Status tracking
    status: SupportRequestStatus;

    // Replies
    replies: SupportReply[];

    createdAt: Date;
    updatedAt: Date;
}


// --- Wazaio WhatsApp Integration ---

/**
 * Admin-curated registry of pre-approved Meta WhatsApp templates.
 * Each row maps a stable app-side `templateKey` to the templateName +
 * language pair as it exists in the Wazaio dashboard, plus the variable
 * mapping that tells the sender how to fill the placeholders.
 *
 * Templates must first be approved by Meta via the Wazaio dashboard
 * BEFORE they can be sent. The doc here is just our local registry.
 */
export type WazaioTemplateCategory = 'utility' | 'marketing' | 'authentication';

export interface WazaioTemplate {
    id: string;                          // templateKey, e.g. 'class_reminder_24h'
    templateName: string;                // Name as approved in Meta/Wazaio dashboard
    templateLanguage: string;            // ISO code, e.g. 'en', 'ar', 'en_US'
    category: WazaioTemplateCategory;
    description?: string;                // Admin-readable, what this template is for
    // Variable map describes which Wazaio body field (field_1..field_N,
    // header_field_1, button_0..button_N, etc.) maps to which app concept.
    // Stored as a free-form Record so we can support any template shape.
    variableMap?: Record<string, string>;
    isActive: boolean;                   // Soft-disable without deleting
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Audit + status tracking for every outbound WhatsApp send. Written by
 * the Cloud Function on every attempt, regardless of success.
 */
export type WazaioMessageKind = 'text' | 'media' | 'template';
export type WazaioMessageStatus = 'pending' | 'sent' | 'failed' | 'delivered' | 'read';

export interface WazaioMessage {
    id: string;
    direction: 'outbound';
    userId?: string;                     // Recipient app user uid (if mapped from phone)
    phoneNumber: string;                 // Normalized E.164-without-+ form
    kind: WazaioMessageKind;
    templateKey?: string;                // When kind='template', the registry key used
    body?: string;                       // Text body or media caption
    mediaUrl?: string;                   // When kind='media'
    mediaType?: 'image' | 'video' | 'document';
    status: WazaioMessageStatus;
    wamid?: string;                      // WhatsApp message id from Meta (if returned)
    error?: string;                      // Populated when status='failed'
    sentBy: string;                      // App user uid who triggered, or 'system'
    triggerReason?: string;              // Free-form label, e.g. 'subscription_expiring_cron'
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Inbound WhatsApp message received via Wazaio webhook. Stores both the
 * parsed envelope and the raw Wazaio payload for debugging / replay.
 */
export type WazaioInboundStatus = 'new' | 'read' | 'actioned';

export interface WazaioInboundMessage {
    id: string;
    direction: 'inbound';
    from: string;                        // Sender phone number (normalized)
    userId?: string;                     // Mapped app user (if phone matches a known user)
    wamid: string;                       // WhatsApp message id
    repliedToWamid?: string;             // If this is a reply to a previous outbound
    body?: string;
    media?: {
        type?: string;
        link?: string;
        caption?: string | null;
        mimeType?: string;
        fileName?: string;
    };
    contactStatus?: 'existing' | 'updated' | 'new'; // From Wazaio's contact envelope
    rawPayload: Record<string, unknown>; // Entire Wazaio JSON
    status: WazaioInboundStatus;
    createdAt: Date;
}

// ============================================================================
// CURRICULUM (Phase A)
// ----------------------------------------------------------------------------
// The curriculum spine — authored by admins via /admin/curriculum, read-only to
// coaches/students. Hierarchy is Unit → Section → Lesson → Technique.
//
// NAMING: we deliberately do NOT reuse "Chapter" here. `CoachClassContext.chapter`
// already exists and means POSITION (Mount, Guard). The sheet's pedagogical
// grouping ("Side Scissor" within "Closed Guard") maps to `Section` — a
// greenfield term with no collision. See docs/curriculum-import notes.
//
// Levels are DISTINCT curricula, not views (Level 1 = white, BBS 1 = blue s1,
// BBS 2 = purple/brown/black). A single technique may recur across levels with
// different context, so `TechniqueLevelMap` is a junction rather than a
// duplicated technique row.
// ============================================================================

export interface CurriculumUnit {
    id: string;
    levelId: string;      // the Level this Unit's tree belongs to (each Level owns
                          // its own Unit → Section → Lesson → Technique tree; a
                          // technique can additionally appear in other Levels via
                          // TechniqueLevelMap). Added for the admin CRUD manager,
                          // which authors one tree per Level.
    name: string;         // "Closed Guard", "Open Guard", "Mount"
    nameAr?: string;      // Arabic name if available
    order: number;        // display order within the level
    colorTag?: string;    // matches Ahmed's yellow/green/pink scheme in the sheets
    createdAt: Date;
    updatedAt: Date;
}

export interface CurriculumSection {
    id: string;
    unitId: string;
    name: string;         // "Side Scissor", "Top Lock", "Butterfly System"
    nameAr?: string;
    order: number;
    createdAt: Date;
    updatedAt: Date;
}

export interface CurriculumLesson {
    id: string;
    sectionId: string;
    name: string;         // "Entry, Controls, and breaking grips" — the sheet's Lesson column verbatim
    nameAr?: string;
    order: number;        // matches the # column in the sheet
    bonusOnly?: boolean;  // true = not part of standard progression, coach's optional extra
    createdAt: Date;
    updatedAt: Date;
}

export interface CurriculumTechnique {
    id: string;
    lessonId: string;
    name: string;         // "Rolling Armbar High Leg" — atomized from slash-separated cells
    nameAr?: string;
    order: number;        // display order within the lesson
    videoRefs?: string[]; // future: link to LMS lesson IDs
    createdAt: Date;
    updatedAt: Date;
}

export interface CurriculumLevel {
    id: string;
    name: string;         // "Level 1", "BBS 1", "BBS 2"
    displayName?: string; // "White Belt Curriculum", etc — for student view
    order: number;
    associatedBelts: Array<{
        belt: 'white' | 'blue' | 'purple' | 'brown' | 'black';
        stripes?: number[];  // [0, 1, 2, 3, 4] — which stripes; omit = all
    }>;
    createdAt: Date;
    updatedAt: Date;
}

// Junction — a single technique may appear at multiple levels with different context
export interface TechniqueLevelMap {
    id: string;
    techniqueId: string;
    levelId: string;
    contextNote?: string;  // "basic mechanics" vs "vs active defense"
    order?: number;        // display order within the level's technique list
    createdAt: Date;
    updatedAt: Date;
}

// The anchors — stored as data so Ahmed can revise after coach interviews
// without a deploy
export interface RatingAnchorSet {
    id: string;             // e.g. "adult-technique-v1", "kid-technique-v1"
    audience: 'adult' | 'kid';
    dimension: 'technique' | 'effort' | 'understanding';
    scale: '1-5-stars' | 'colored-dots';
    anchors: Array<{
        value: number | 'red' | 'yellow' | 'green';
        label: string;       // "Working on it"
        labelAr?: string;
        description: string; // "Knows the shape, can't complete against resistance"
        descriptionAr?: string;
    }>;
    version: number;
    activeAt: Date;
    supersedes?: string;  // previous version's id
    createdAt: Date;
    updatedAt: Date;
}

// ============================================================================
// VOICE CALLING ENGINE (Vapi) — Task #46
// ----------------------------------------------------------------------------
// Automated retention / onboarding phone calls placed via Vapi, logged per
// member with transcript, sentiment, and extracted member requests. Ships
// INERT behind systemSettings/voiceCalls.voiceCallsEnabled (default false) +
// pilotMode (default true, empty allowlist). Every call goes through the single
// vapiPlaceCall() choke point in functions/. See CLAUDE.md §11 Task #46.
// ============================================================================

export type VoiceCallKind = 'retention_day7' | 'retention_week4' | 'onboarding_day15' | 'announcement_opening';

// Grand-Opening campaign (kind=announcement_opening) — the member's stated intent.
export type VoiceCallAttendingIntent = 'opening_day' | 'specific_day' | 'maybe' | 'no';

export type VoiceCallStatus =
    | 'queued'       // created, not yet handed to Vapi
    | 'dialing'      // vapiPlaceCall accepted, ringing
    | 'in_progress'  // call answered / mid-call (from webhook status-update)
    | 'completed'    // end-of-call report processed
    | 'no_answer'    // ended without pickup
    | 'failed'       // provider/dial error
    | 'no_result'    // placed but no end-of-call report after 30m (reconcile)
    | 'canceled';

// From the structured-output extraction (VOICE_CALL_EXTRACTION_SCHEMA.outcome).
export type VoiceCallOutcome =
    | 'will_renew' | 'renewed_during_call' | 'freeze_requested' | 'price_objection'
    | 'schedule_conflict' | 'motivation' | 'kid_motivation' | 'complaint_escalated'
    | 'callback_requested' | 'refused' | 'no_engagement' | 'feedback_collected';

export type VoiceCallRequestType =
    | 'freeze' | 'schedule_change' | 'branch_change' | 'pricing' | 'complaint'
    | 'question' | 'callback' | 'coach_talk' | 'other';

export interface VoiceCallSentiment {
    overall: 'positive' | 'neutral' | 'negative';
    score: number;   // -1..1
    reason: string;
}

export interface VoiceCallTranscriptTurn {
    i: number;                          // turn index (0-based, chronological)
    role: 'assistant' | 'member';
    text: string;
    tsMs: number;                       // ms offset from call start
}

export interface VoiceCallRequest {
    id: string;
    type: VoiceCallRequestType;
    quote: string;                      // verbatim Arabic quote from the call
    turnIndexes: number[];              // transcript turns containing it ([] if unmatched)
    detail: string;                     // 1-line English summary
    needsHuman: boolean;
    status: 'open' | 'done';            // admin can mark done
}

export interface VoiceCall {
    id: string;                         // callDocId
    memberId: string;
    memberName: string;                 // denormalized for list views
    kind: VoiceCallKind;
    assistantId: string;
    vapiCallId?: string;                // the ONLY Vapi id we store (recordings fetched on demand)
    status: VoiceCallStatus;
    placedAt: Date;
    startedAt?: Date;
    endedAt?: Date;
    durationSec?: number;
    costUsd?: number;
    outcome?: VoiceCallOutcome;
    sentiment?: VoiceCallSentiment;
    transcript?: VoiceCallTranscriptTurn[];   // built from artifact.messages, turn-by-turn
    requests?: VoiceCallRequest[];             // extracted member requests (highlight source)
    summary?: string;                          // 2-sentence English summary
    needsHuman?: boolean;                      // any request.needsHuman || outcome complaint_escalated
    // Retry / suppression bookkeeping (see engines).
    retryCount?: number;                       // no-answer retries used for this stage
    lapseCycleId?: string;                     // groups calls within one lapse cycle
    // Grand-Opening campaign (kind=announcement_opening).
    campaignId?: string;                       // groups a one-off broadcast; dedup key (one call per member per campaign)
    attendingIntent?: VoiceCallAttendingIntent; // opening-day attendance forecast
    committedClass?: string;                   // day/class the member committed to
    friendInterest?: boolean;                  // wants to bring a friend
    createdAt: Date;
    updatedAt: Date;
}

/**
 * Admin config for the voice engine — systemSettings/voiceCalls.
 * The engine reads this on every tick; nothing dials unless voiceCallsEnabled
 * is true AND (pilotMode is false OR the target number is on pilotAllowlist).
 */
export interface VoiceCallSettings {
    voiceCallsEnabled: boolean;          // MASTER TOGGLE — default false (inert)
    pilotMode: boolean;                  // default true — only dial pilotAllowlist numbers
    pilotAllowlist: string[];            // E.164 numbers dialable during pilot (Ahmed's own)
    phoneNumberId: string;               // Vapi phone-number UUID (NOT E.164) — the caller ID
    globalDailyCap: number;              // max calls placed per Cairo day (default 30)
    // assistantId per call kind — design for N assistants (Salma retention,
    // Maryam onboarding, Salma — Grand Opening, …). Empty string = kind disabled.
    assistantByKind: Record<VoiceCallKind, string>;
    // Grand-Opening campaign config (kind=announcement_opening).
    campaignExpiredCutoffDays?: number;   // include members whose sub expired ≤ N days ago (default 60)
    campaignDailyCap?: number;            // max announcement calls per run (default 80)
    campaignEndsAt?: string;              // 'YYYY-MM-DD' Cairo hard stop (default 2026-08-19)
    updatedAt?: Date;
}

/**
 * Dynamic knowledge base for the voice agents (Task #46 delta). Managed from
 * admin Settings; compiled and injected as {{knowledgeBase}} on EVERY call for
 * both assistants — edits take effect next call, no prompt republish.
 */
export type VoiceKbEntryType = 'text' | 'link' | 'qa';

export interface VoiceKbEntry {
    id: string;
    type: VoiceKbEntryType;
    title: string;                       // short label for the settings list
    content?: string;                    // text: the info (Arabic); qa: the answer
    question?: string;                   // qa only: the member's verbatim question
    // link entries only:
    url?: string;
    fetchedContent?: string;             // cleaned server-side extract (refreshKbLinks)
    lastFetchedAt?: Date;
    fetchStatus?: 'ok' | 'error' | 'pending';
    active: boolean;
    order: number;
    updatedAt: Date;
    updatedBy?: string;
}
